import React, { useCallback, useEffect, useState } from 'react';
import {
  Shield,
  RefreshCw,
  AlertTriangle,
  Users,
  UserCheck,
  Inbox,
  XOctagon,
  PlayCircle,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';

interface Row {
  [k: string]: any;
}

const severityCls = (s: string) =>
  s === 'critical'
    ? 'bg-red-50 text-red-700'
    : s === 'warning'
    ? 'bg-amber-50 text-amber-700'
    : 'bg-emerald-50 text-emerald-700';

const statusCls = (s: string) =>
  s === 'dead'
    ? 'bg-red-50 text-red-700'
    : s === 'synced'
    ? 'bg-emerald-50 text-emerald-700'
    : 'bg-amber-50 text-amber-700';

/**
 * Admin > Security panel.
 *
 * Read-only window on the security + lead-capture plumbing:
 *   - audit log (mkt_security_events): bot challenges, rate-limit blocks,
 *     CRM sync results, dead-letter alerts,
 *   - CRM retry queue (mkt_crm_queue) with a manual "run worker now" button,
 *   - anonymous visitor list (mkt_visitors) with attribution.
 *
 * Every table here is admin-only at the RLS level, so a non-admin session
 * simply gets zero rows even if it calls the API directly.
 */
const AdminSecurity: React.FC = () => {
  const { t } = useI18n();
  const [events, setEvents] = useState<Row[]>([]);
  const [queue, setQueue] = useState<Row[]>([]);
  const [visitors, setVisitors] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const [e, q, v] = await Promise.all([
      supabase.from('mkt_security_events').select('*').order('created_at', { ascending: false }).limit(60),
      supabase.from('mkt_crm_queue').select('*').order('created_at', { ascending: false }).limit(40),
      supabase.from('mkt_visitors').select('*').order('last_seen', { ascending: false }).limit(25),
    ]);
    setEvents(e.data || []);
    setQueue(q.data || []);
    setVisitors(v.data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runRetry = async () => {
    setRunning(true);
    setNote('');
    try {
      const { data } = await supabase.functions.invoke('crm-retry', { body: {} });
      setNote(`${t('sec.retryDone')} — ${JSON.stringify(data || {})}`);
    } catch {
      setNote(t('sec.retryDone'));
    }
    setRunning(false);
    load();
  };

  const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
  const blocked24 = events.filter(
    (e) => e.severity !== 'info' && new Date(e.created_at).getTime() > dayAgo
  ).length;
  const identified = visitors.filter((v) => v.identified_at).length;
  const pending = queue.filter((q) => q.status === 'pending').length;
  const dead = queue.filter((q) => q.status === 'dead').length;

  const kpis = [
    { icon: Users, label: t('sec.visitors'), value: visitors.length },
    { icon: UserCheck, label: t('sec.identified'), value: identified },
    { icon: AlertTriangle, label: t('sec.blocked24'), value: blocked24 },
    { icon: Inbox, label: t('sec.queuePending'), value: pending },
    { icon: XOctagon, label: t('sec.queueDead'), value: dead },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
            <Shield className="h-5 w-5 text-orange-500" /> {t('sec.title')}
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">{t('sec.sub')}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={runRetry}
            disabled={running}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            <PlayCircle className="h-4 w-4" /> {t('sec.retryNow')}
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> {t('sec.refresh')}
          </button>
        </div>
      </div>

      {note && (
        <p className="mt-4 rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700">{note}</p>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {kpis.map((k) => (
          <div key={k.label} className="rounded-2xl border border-slate-200 bg-white p-5">
            <k.icon className="h-5 w-5 text-orange-500" />
            <p className="mt-3 text-2xl font-extrabold text-slate-900">{k.value}</p>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{k.label}</p>
          </div>
        ))}
      </div>

      {/* CRM retry queue */}
      <h3 className="mt-10 text-lg font-extrabold text-slate-900">{t('sec.queueTitle')}</h3>
      <p className="mt-1 max-w-3xl text-sm text-slate-600">{t('sec.queueSub')}</p>
      <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        {queue.length === 0 ? (
          <p className="p-6 text-center text-sm text-slate-500">{t('common.none')}</p>
        ) : (
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">{t('sec.event')}</th>
                <th className="px-4 py-3">{t('common.email')}</th>
                <th className="px-4 py-3">{t('common.status')}</th>
                <th className="px-4 py-3">{t('sec.attempts')}</th>
                <th className="px-4 py-3">{t('sec.lastError')}</th>
                <th className="px-4 py-3">{t('sec.when')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {queue.map((q) => (
                <tr key={q.id}>
                  <td className="px-4 py-3 font-semibold text-slate-800">{q.event_name}</td>
                  <td className="px-4 py-3 text-slate-600">{q.email || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${statusCls(q.status)}`}>
                      {q.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {q.attempts}/{q.max_attempts}
                  </td>
                  <td className="max-w-xs truncate px-4 py-3 text-xs text-slate-500">{q.last_error || '—'}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">
                    {new Date(q.created_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Audit log */}
      <h3 className="mt-10 text-lg font-extrabold text-slate-900">{t('sec.eventsTitle')}</h3>
      <p className="mt-1 max-w-3xl text-sm text-slate-600">{t('sec.eventsSub')}</p>
      <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        {events.length === 0 ? (
          <p className="p-6 text-center text-sm text-slate-500">{t('sec.noEvents')}</p>
        ) : (
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">{t('sec.event')}</th>
                <th className="px-4 py-3">{t('sec.severity')}</th>
                <th className="px-4 py-3">{t('common.email')}</th>
                <th className="px-4 py-3">{t('sec.detail')}</th>
                <th className="px-4 py-3">{t('sec.when')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {events.map((e) => (
                <tr key={e.id}>
                  <td className="px-4 py-3 font-semibold text-slate-800">{e.event_type}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${severityCls(e.severity)}`}>
                      {e.severity}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{e.email || '—'}</td>
                  <td className="max-w-md truncate px-4 py-3 text-xs text-slate-500">{e.detail || '—'}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">
                    {new Date(e.created_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Anonymous visitors */}
      <h3 className="mt-10 text-lg font-extrabold text-slate-900">{t('sec.visitorsTitle')}</h3>
      <p className="mt-1 max-w-3xl text-sm text-slate-600">{t('sec.visitorsSub')}</p>
      <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        {visitors.length === 0 ? (
          <p className="p-6 text-center text-sm text-slate-500">{t('common.none')}</p>
        ) : (
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">{t('sec.source')}</th>
                <th className="px-4 py-3">{t('sec.landing')}</th>
                <th className="px-4 py-3">{t('sec.pages')}</th>
                <th className="px-4 py-3">{t('common.type')}</th>
                <th className="px-4 py-3">{t('common.email')}</th>
                <th className="px-4 py-3">{t('sec.when')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visitors.map((v) => (
                <tr key={v.visitor_id}>
                  <td className="px-4 py-3 font-semibold text-slate-800">
                    {v.utm_source || v.referrer || 'direct'}
                  </td>
                  <td className="max-w-xs truncate px-4 py-3 text-slate-600">{v.landing_page || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{v.page_views}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {v.device} · {v.language}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{v.email || '—'}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">
                    {new Date(v.last_seen).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminSecurity;
